/**
 * Mobile cart bar — the bottom-pinned summary the right rail's cart can't show
 * on small screens (the rail is `hidden lg:flex`). Cart icon + subtotal + item
 * count on the left, "Review & Checkout" on the right. Hidden on desktop.
 *
 * Presented as a drawer sheet (curved top, border, upward shadow); it does not
 * behave like one — it stays pinned and never opens or dismisses.
 *
 * Totals span EVERY campus the admin manages (same union as the right rail
 * and the All-campuses checkout), with a campus count beside the item count
 * when more than one campus holds lines.
 */

import { Button } from '@ordo/ui/button'
import { ShoppingCart } from 'lucide-react'
import type { ReactElement } from 'react'
import { useNavigate } from 'react-router'

import type { AdminSchool } from '@/api/school-orders'
import { crossCampusCartSummary } from '@/lib/school-orders/checkout-scope'
import { formatCents } from '@/lib/school-orders/money'
import { useSchoolCart } from '@/stores/school-cart'

export function MobileCartBar({
  school,
  schools,
}: {
  school: AdminSchool
  /** All the admin's campuses; the bar falls back to the selected one alone. */
  schools?: AdminSchool[]
}): ReactElement {
  const navigate = useNavigate()
  const linesByLocation = useSchoolCart((s) => s.linesByLocation)
  const scope = schools && schools.length > 0 ? schools : [school]
  // Tax-inclusive, matching the right rail's Total and the family bar —
  // every single-amount cart surface shows what checkout will charge. The
  // bar has no room for the "Est." label or its tooltip — the explanation
  // lives on the two surfaces that have a tax ROW, and checkout (where the
  // order is actually placed) is one of them.
  const summary = crossCampusCartSummary(scope.map((s) => ({ school: s, lines: linesByLocation[s.id] ?? [] })))
  const campusCount = summary.campuses.length
  return (
    // `-mx-4` cancels the page Shell's `px-4` so the bar bleeds to the screen
    // edges; its own `px-4` keeps the content inset. Styled as a drawer sheet —
    // curved top corners, a border, and a shadow cast upward over the day list.
    <div className="-mx-4 flex shrink-0 items-center justify-between gap-3 rounded-t-2xl border border-b-0 bg-background px-4 py-3 shadow-[0_-6px_20px_-8px_rgb(0_0_0/0.25)] lg:hidden">
      <div className="flex items-center gap-3">
        <ShoppingCart className="h-6 w-6 shrink-0 text-foreground" strokeWidth={1.75} />
        <div className="flex flex-col leading-tight">
          <span className="text-base font-semibold tabular-nums text-foreground">
            {summary.totalCents !== null ? formatCents(summary.totalCents, summary.currency) : '—'}
          </span>
          <span className="text-xs text-muted-foreground">
            {summary.qty} {summary.qty === 1 ? 'item' : 'items'}
            {campusCount > 1 ? ` · ${campusCount} campuses` : ''}
          </span>
        </div>
      </div>
      <Button onClick={() => navigate('/orders/school/checkout')}>Review &amp; Checkout</Button>
    </div>
  )
}
